import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import candidateService from "../service-api/candidateService";
import candidateSkillSetsService from "../service-api/candidateSkillSetsService";
import { useParams } from "react-router-dom";
import {
  addLinkedCandidate,
  fetchLinkedCandidatesByReqID,
} from "./requisitionSlicer";
import { fetchAllCandidateSkillSets } from "./candidateSkillSetsSlicer";
import requisitionService from "../service-api/requisitionService";

// Initial state
const initialState = {
  data: [],
  error: null,
  isLoading: false,
  isSingleCandidateLoading: true,
  candidateEmail: "",
  singleCandidate: {},
  candidateID: "",
  resumeAnnotationID: "",
  resumeData: {},
  candidateSkills: [],
  isDeleteSuccess: true,
  isEditBasic: true,
  aiResumeData: {},
  isResumeLoading: false,
  declarationData: {},
  isGeneratingScore: false,
};

export const fetchCandidates = createAsyncThunk(
  "data/fetchCandidates",
  async () => {
    const response = await candidateService.fetchCandidates();

    return response.data;
  }
);

export const addCandidate = createAsyncThunk(
  "data/addCandidate",
  async (
    { apiData, resumeApiData, skillsList, addAndLinkCandidate, reqID },
    { dispatch, getState }
  ) => {
    try {
      // Call the API to add a candidate
      const response = await candidateService.addCandidate(apiData);

      // Extract the generated candidate ID from the response
      const candidateID = response.data?.contactid;

      if (candidateID) {
        // Update resume object with the candidate ID
        const updatedResumeObj = {
          ...resumeApiData,
          "objectid_contact@odata.bind": `/contacts(${candidateID})`,
        };

        // Dispatch the addResume action
        await dispatch(addResume(updatedResumeObj));
      }

      if (candidateID && skillsList.length > 0) {
        const promises = skillsList.map((item) => {
          const updatedSkillSetObj = {
            itl_experiencerange: item.itl_experiencerange,
            "itl_CandidateID@odata.bind": `/contacts(${candidateID})`,
            "itl_SkillSet@odata.bind": `/itl_skillsetmasters(${item.itl_skillset})`,
          };
          return dispatch(addCandidateSkillSet(updatedSkillSetObj));
        });

        await Promise.all(promises);
      }

      if (reqID) {
        const newLinkCandidateObj = {
          "itl_CandidateID@odata.bind": `/contacts(${candidateID})`,
          "itl_RequisitionID@odata.bind": `/itl_requisitions(${reqID})`,
        };

        await dispatch(addLinkedCandidate(newLinkCandidateObj));
        await dispatch(fetchLinkedCandidatesByReqID(reqID));
      }

      // Fetch updated candidate list
      dispatch(fetchCandidates());
      dispatch(fetchAllCandidateSkillSets());
      return response.data;
    } catch (error) {
      console.error("Error adding candidate:", error);
      throw error;
    }
  }
);

export const generateJobMatchingScore = createAsyncThunk(
  "data/generateJobMatchingScore",
  async ({ candidateID, reqID }) => {
    const response = await requisitionService.generateLinkedCandidateScore(
      candidateID,
      reqID,
    );
    return response.data;
  },
);

export const removeJobMatchingScore = createAsyncThunk(
  "data/removeJobMatchingScore",
  async (candidateID) => {
    const response = await requisitionService.deleteUnllinkedCandidateScore(
      candidateID,
    );
    return response.data;
  },
);


export const updateCandidateFormDetails = createAsyncThunk(
  "data/updateCandidateFormDetails",
  async ({ id, formData }, { dispatch }) => {
    const response = await candidateService.updateCandidate(id, formData);
    // await dispatch(fetchCandidateByID(id)).unwrap();
    return response.data;
  }
);

export const fetchCandidateEmailByID = createAsyncThunk(
  "data/fetchcandidatecmailbyid",
  async (id) => {
    const response = await candidateService.fetchCandidateEmailByID(id);
    return response.data;
  }
);

export const fetchCandidateDeclarationByID = createAsyncThunk(
  "data/fetchCandidateDeclarationByID",
  async (id) => {
    const response = await candidateService.fetchCandidateDeclarationByID(id);
    return response.data;
  }
);

export const fetchCandidateByID = createAsyncThunk(
  "data/fetchcandidatebyid",
  async (id) => {
    const response = await candidateService.fetchCandidateByID(id);
    return response.data;
  }
);

export const addResume = createAsyncThunk(
  "data/addannotation",
  async (obj, { dispatch }) => {
    const response = await candidateService.addResume(obj);
    return response.data;
  }
);

export const getResume = createAsyncThunk(
  "data/getannotation",
  async (id, { dispatch }) => {
    const response = await candidateService.getResume(id);
    return response.data;
  }
);

export const updateResume = createAsyncThunk(
  "data/updateresume",
  async ({ annotationID, resumeApiData }) => {
    const response = await candidateService.updateResume(
      annotationID,
      resumeApiData
    );
    return response.data;
  }
);

export const updateCandidate = createAsyncThunk(
  "data/updatecandidate",
  async (
    { id, apiData, annotationID, resumeApiData, skillsList, noToast },
    { dispatch }
  ) => {
    const response = await candidateService.updateCandidate(id, apiData);

    // Dispatch the resume update
    if (resumeApiData && annotationID) {
      await dispatch(updateResume({ annotationID, resumeApiData })).unwrap();
      // dispatch(updateResumeTextData(id));
    }

    // Fetch updated candidate details and skill sets
    await dispatch(fetchCandidates());
    await dispatch(fetchCandidateByID(id)).unwrap();
    await dispatch(fetchCandidateSkillSets(id)).unwrap();
    await dispatch(getResume(id)).unwrap();

    return response.data;
  }
);

export const deleteCandidate = createAsyncThunk(
  "data/deleteCandidate",
  async (id, { dispatch }) => {
    const response = await candidateService.deleteCandidate(id);
    dispatch(fetchCandidates());
    return response.data;
  }
);

export const fetchCandidateSkillSets = createAsyncThunk(
  "data/fetchSkillSets",
  async (id) => {
    const response = await candidateSkillSetsService.fetchCandidateSkillSets(
      id
    );
    return response.data;
  }
);

export const addCandidateSkillSet = createAsyncThunk(
  "data/addCandidateSkillset",
  async (obj, { dispatch }) => {
    const response = await candidateSkillSetsService.addCandidateSkillSkillSet(
      obj
    );
    dispatch(fetchCandidates());
    return response.data;
  }
);

export const updateCandidateSkillSet = createAsyncThunk(
  "data/updatecandidateskilset",
  async ({ skillID, skillsetObj }, { dispatch }) => {
    const response =
      await candidateSkillSetsService.updateCandidateSkillSkillSet(
        skillID,
        skillsetObj
      );

    dispatch(fetchCandidates());
    return response.data;
  }
);

export const deleteCandidateSkillSet = createAsyncThunk(
  "data/deleteCandidateSkillset",
  async (id, { dispatch }) => {
    const response =
      await candidateSkillSetsService.deleteCandidateSkillSkillSet(id);
    dispatch(fetchCandidates());
    return response.data;
  }
);

export const linkCandidateToRequisition = createAsyncThunk(
  "data/linkCandidateToRequisition",
  async (id, { dispatch }) => {
    const response =
      await candidateSkillSetsService.deleteCandidateSkillSkillSet(id);
    dispatch(fetchCandidates());
    return response.data;
  }
);

export const readResumeData = createAsyncThunk(
  "data/readResumeData",
  async (data, { dispatch }) => {
    const response = await candidateService.parseResumeData(data);
    return response.data;
  }
);

export const addResumeTextData = createAsyncThunk(
  "data/addResumeTextData",
  async (obj, { dispatch }) => {
    const response = await candidateService.saveResumeTextData(obj);
    return response.data;
  }
);

export const updateResumeTextData = createAsyncThunk(
  "data/updateResumeTextData",
  async (id, { getState }) => {
    const state = getState();
    const aiResumeData = state.candidates.aiResumeData;
    if (Object.keys(aiResumeData).length) {
      const response = await candidateService.updateOrPatchResumeTextData(
        id,
        aiResumeData
      );
      return response.data;
    }
  }
);

export const updateCandidateDeclaration = createAsyncThunk(
  "data/updateCandidateDeclaration",
  async ({ id, formData }) => {
    const response = await candidateService.updateCandidateDeclaration(
      id,
      formData
    );
    return response.data;
  }
);

export const updateCandidateSignature = createAsyncThunk(
  "data/updateCandidateSignature",
  async ({ id, signatureFile }) => {
    const response = await candidateService.updateCandidateSignature(
      id,
      signatureFile
    );
    return response.data;
  }
);

// Create slice
const candidateSlice = createSlice({
  name: "candidate",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get all Candidates
    builder
      .addCase(fetchCandidates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.value;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Get Candidate Email by ID
    builder
      .addCase(fetchCandidateEmailByID.pending, (state) => {
        state.isSingleCandidateLoading = true;
      })
      .addCase(fetchCandidateEmailByID.fulfilled, (state, action) => {
        state.isSingleCandidateLoading = false;
        state.candidateEmail = action.payload.itl_emailid;
      })
      .addCase(fetchCandidateEmailByID.rejected, (state, action) => {
        state.isSingleCandidateLoading = false;
        state.error = action.error.message;
      });

    // Get Candidate Declaration by ID
    builder
      .addCase(fetchCandidateDeclarationByID.pending, (state) => {
        state.isSingleCandidateLoading = true;
      })
      .addCase(fetchCandidateDeclarationByID.fulfilled, (state, action) => {
        state.isSingleCandidateLoading = false;
        state.declarationData = action.payload;
      })
      .addCase(fetchCandidateDeclarationByID.rejected, (state, action) => {
        state.isSingleCandidateLoading = false;
        state.error = action.error.message;
      });

    // Get Candidate by ID
    builder
      .addCase(fetchCandidateByID.pending, (state) => {
        if (!state.singleCandidate || Object.keys(state.singleCandidate).length === 0) {
          state.isSingleCandidateLoading = true;
        }
      })
      .addCase(fetchCandidateByID.fulfilled, (state, action) => {
        state.isSingleCandidateLoading = false;
        state.singleCandidate = action.payload;
      })
      .addCase(fetchCandidateByID.rejected, (state, action) => {
        state.isSingleCandidateLoading = false;
        state.error = action.error.message;
      });

    // Add Candidate
    builder
      .addCase(addCandidate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addCandidate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidateID = action.payload.contactid;
        state.data = action.payload.value;
        toast.success("New Candidate Added");
      })
      .addCase(addCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Update Candidate
    builder
      .addCase(updateCandidate.pending, (state) => {
        state.isLoading = true;
        state.isEditBasic = true;
      })
      .addCase(updateCandidate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isEditBasic = false;
        // state.data = action.payload.value;
        // if (!action.meta.arg.noToast) {
        //   toast.info("Candidate Details Updated");
        // }
      })
      .addCase(updateCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.isEditBasic = false;
        state.error = action.error.message;
      });

    // Delete Candidate
    builder
      .addCase(deleteCandidate.pending, (state) => {
        state.isLoading = true;
        state.isDeleteSuccess = true;
      })
      .addCase(deleteCandidate.fulfilled, (state, action) => {
        state.isDeleteSuccess = false;
        state.isLoading = false;
        toast.error("Candidate Deleted");
      })
      .addCase(deleteCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Add Resume
    builder
      .addCase(addResume.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addResume.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidateID = action.payload.value;
      })
      .addCase(addResume.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Fetch Resume
    builder
      .addCase(getResume.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getResume.fulfilled, (state, action) => {
        state.resumeData = action.payload.value[0].Contact_Annotation[0];
        // console.log(state.resumeData);
        // console.log(action.payload.value[0].Contact_Annotation[0].a);
        state.isLoading = false;
      })
      .addCase(getResume.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Update Resume
    builder
      .addCase(updateResume.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateResume.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateResume.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Get Candidate Skills by ID
    builder
      .addCase(fetchCandidateSkillSets.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCandidateSkillSets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidateSkills = action.payload.value;
      })
      .addCase(fetchCandidateSkillSets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Add Candidate Skill
    builder
      .addCase(addCandidateSkillSet.pending, (state) => {
        state.isLoading = true;
        state.isEditBasic = true;
      })
      .addCase(addCandidateSkillSet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isEditBasic = true;
        state.data = action.payload.value;
      })
      .addCase(addCandidateSkillSet.rejected, (state, action) => {
        state.isLoading = false;
        state.isEditBasic = true;
        state.error = action.error.message;
      });

    // Add Candidate Skill
    builder
      .addCase(updateCandidateSkillSet.pending, (state) => {
        state.isLoading = true;
        state.isEditBasic = true;
      })
      .addCase(updateCandidateSkillSet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isEditBasic = false;
      })
      .addCase(updateCandidateSkillSet.rejected, (state, action) => {
        state.isLoading = false;
        state.isEditBasic = false;
        state.error = action.error.message;
      });

    // Delete Candidate Skill
    builder
      .addCase(deleteCandidateSkillSet.pending, (state) => {
        state.isLoading = true;
        state.isEditBasic = true;
      })
      .addCase(deleteCandidateSkillSet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isEditBasic = false;
        // state.data = action.payload.value;
      })
      .addCase(deleteCandidateSkillSet.rejected, (state, action) => {
        state.isLoading = false;
        state.isEditBasic = false;
        state.error = action.error.message;
      });

    //Read Resume Data
    builder
      .addCase(readResumeData.pending, (state) => {
        state.isResumeLoading = true;
      })
      .addCase(readResumeData.fulfilled, (state, action) => {
        state.isResumeLoading = false;
        state.aiResumeData = action.payload;
      })
      .addCase(readResumeData.rejected, (state, action) => {
        state.isResumeLoading = false;
        state.error = action.error.message;
      });

    //Update Candidate Declaration
    builder
      .addCase(updateCandidateDeclaration.pending, (state) => { })
      .addCase(updateCandidateDeclaration.fulfilled, (state, action) => { })
      .addCase(updateCandidateDeclaration.rejected, (state, action) => {
        state.error = action.error.message;
      });


    builder.addCase(generateJobMatchingScore.pending, (state, action) => {
      state.isGeneratingScore = true;
    });

    builder.addCase(generateJobMatchingScore.fulfilled, (state, action) => {
      state.isGeneratingScore = false;
    });

    builder.addCase(generateJobMatchingScore.rejected, (state, action) => {
      state.isGeneratingScore = false;
      state.error = action.error.message;
    });
  },
});

export default candidateSlice.reducer;
